import { useState, type ChangeEvent } from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from '@/components/ui/Toast';
import { cn } from '@/lib/utils';

interface MediaFieldProps {
  value: string | null;
  onChange: (mediaId: string | null) => void;
  bucket?: 'public' | 'cvs';
  accept?: string;
  hint?: string;
  label?: string;
  className?: string;
}

const MAX_BYTES = 10 * 1024 * 1024;

const ACCEPTED_BY_BUCKET: Record<string, string[]> = {
  public: ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml', 'image/avif'],
  cvs: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
};

export function MediaField({ value, onChange, bucket = 'public', accept, hint, label, className }: MediaFieldProps) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    setError(null);
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setError('File must be ≤ 10 MB.');
      return;
    }
    const allowed = accept ? accept.split(',').map((s) => s.trim()) : ACCEPTED_BY_BUCKET[bucket] ?? [];
    if (!allowed.includes(file.type)) {
      setError(`Unsupported file type (${file.type || 'unknown'}).`);
      return;
    }

    setUploading(true);
    const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}-${safe}`;

    const { error: upErr } = await supabase.storage.from(bucket).upload(path, file, {
      contentType: file.type,
      cacheControl: '31536000',
      upsert: false,
    });
    if (upErr) {
      setError(upErr.message);
      setUploading(false);
      return;
    }

    // Record in media_assets for cataloging.
    const { data: row, error: insErr } = await supabase
      .from('media_assets')
      .insert({
        bucket,
        path,
        filename: file.name,
        alt: file.name,
        mime: file.type,
        bytes: file.size,
        tags: [],
      })
      .select('id')
      .single();
    if (insErr || !row) {
      // Upload succeeded but catalog failed; keep the file but warn.
      toast.warning('File uploaded but media_assets record failed. Refresh and try again.');
      setUploading(false);
      return;
    }

    setUploading(false);
    onChange(row.id as string);
    if (file.type.startsWith('image/')) {
      setPreview(URL.createObjectURL(file));
    }
    toast.success('File uploaded.');
    e.target.value = '';
  }

  function clear() {
    onChange(null);
    setPreview(null);
    setError(null);
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {label && <label className="text-xs font-medium text-fg-muted">{label}</label>}

      <div className="flex items-center gap-3">
        {value ? (
          <div className="flex items-center gap-3 rounded-md border border-border bg-bg-elevated px-3 py-2">
            {preview || isImageMime(value) ? (
              <img
                src={preview ?? publicAssetUrl(bucket, value ?? '')}
                alt="Selected media"
                className="h-10 w-10 rounded object-cover"
              />
            ) : (
              <ImageIcon className="h-5 w-5 text-gold-500" />
            )}
            <span className="text-sm text-fg-muted">Media linked</span>
            <button
              type="button"
              onClick={clear}
              aria-label="Remove media"
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-fg-muted hover:bg-bg-subtle hover:text-fg"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-border-strong bg-bg-elevated px-4 py-3 text-sm transition-colors hover:border-gold-500/40">
            <Upload className="h-4 w-4 text-gold-500" />
            <span className="font-medium text-fg">
              {uploading ? 'Uploading…' : 'Click to upload'}
            </span>
            <input
              type="file"
              accept={accept ?? (ACCEPTED_BY_BUCKET[bucket] ?? []).join(',')}
              onChange={onFile}
              disabled={uploading}
              className="sr-only"
            />
          </label>
        )}
      </div>

      {hint && <p className="text-xs text-fg-subtle">{hint}</p>}
      {error && <p className="text-xs text-danger" role="alert">{error}</p>}
    </div>
  );
}

function publicAssetUrl(bucket: string, mediaId: string): string {
  const base = (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? '';
  return `${base}/storage/v1/object/public/${bucket}/${mediaId}`;
}

function isImageMime(_value: string | null | undefined): boolean {
  // Heuristic: actual MIME is stored in media_assets.mime; without it we assume image.
  return true;
}